import React from 'react';
import { useCMS } from '../../context/CMSContext';
import { IconRenderer } from '../common/IconRenderer';
import { getComputedTextStyle } from '../../utils/typographyHelper';
import { ScrollReveal } from '../common/ScrollReveal';

export const PhilosophySection: React.FC = () => {
  const { data } = useCMS();
  const { philosophy, typography } = data;

  const tagStyle = getComputedTextStyle(
    typography?.philosophyTag,
    typography?.sectionTags,
    { fontSize: 11, fontWeight: '500', fontColor: '#827f79', letterSpacing: '0.25em', textTransform: 'uppercase' }
  );

  const headingStyle = getComputedTextStyle(
    typography?.philosophyHeading,
    typography?.h2 || typography?.sectionHeadings,
    {
      fontSize: 52,
      fontWeight: typography?.allHeadingFontWeight || '300',
      fontColor: '#0a122a',
      lineHeight: 1.12,
      letterSpacing: '-0.02em'
    }
  );

  const defaultPillarTitleStyle = getComputedTextStyle(
    typography?.philosophyPillarTitle,
    typography?.h4,
    { fontSize: 11, fontWeight: '600', fontColor: '#0a122a', letterSpacing: '0.18em', textTransform: 'uppercase' }
  );

  const defaultParagraphStyle = getComputedTextStyle(
    typography?.philosophyParagraphs,
    typography?.bodyParagraphs || typography?.sectionParagraphs,
    { fontSize: 12, fontWeight: '300', fontColor: '#0a122a', lineHeight: 1.65 }
  );

  const hasLine1 = Boolean(philosophy.headingLine1 && philosophy.headingLine1.trim());
  const hasLine2 = Boolean(philosophy.headingLine2 && philosophy.headingLine2.trim());
  const hasLine3 = Boolean(philosophy.headingLine3 && philosophy.headingLine3.trim());
  const hasAnyHeading = hasLine1 || hasLine2 || hasLine3;
  const hasTag = Boolean(philosophy.tag && philosophy.tag.trim());

  return (
    <section id="philosophy" className="py-24 md:py-32 bg-[#f7f2e8] text-[#1c1d20]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          
          {/* Left Column: Heading & Tag */}
          <div className="lg:col-span-4 space-y-4">
            {hasTag && (
              <ScrollReveal delay={0}>
                <div className="flex items-center space-x-3" style={tagStyle}>
                  <span>{philosophy.tag}</span>
                  <span className="w-12 h-[1px] bg-current opacity-60 inline-block"></span>
                </div>
              </ScrollReveal>
            )}
            {hasAnyHeading ? (
              <ScrollReveal delay={120}>
                <h2
                  className="font-jost tracking-tight"
                  style={headingStyle}
                >
                  {hasLine1 && <div>{philosophy.headingLine1}</div>}
                  {hasLine2 && <div>{philosophy.headingLine2}</div>}
                  {hasLine3 && <div>{philosophy.headingLine3}</div>}
                </h2>
              </ScrollReveal>
            ) : null}
          </div>

          {/* Right Columns: 4 Philosophy Pillars inside bordered enclosure matching screenshot */}
          <div className="lg:col-span-8">
            <ScrollReveal delay={180}>
              <div className="border border-[#ded8cc] bg-[#f7f2e8] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#ded8cc]">
                {philosophy.pillars.map((pillar, idx) => {
                  const iconSize = philosophy.commonIconSize ?? pillar.iconSize ?? 28;
                  const iconStrokeWidth = philosophy.commonIconStrokeWidth ?? pillar.iconStrokeWidth ?? 1.25;
                  const iconColor = philosophy.commonIconColor || pillar.iconColor || '#a88345';
                  const pillarTitleStyle = getComputedTextStyle(pillar.titleStyle, typography?.philosophyPillarTitle, defaultPillarTitleStyle);
                  const pillarDescStyle = getComputedTextStyle(pillar.descStyle, typography?.philosophyParagraphs, defaultParagraphStyle);

                  return (
                    <div
                      key={pillar.id || idx}
                      className="p-6 sm:p-7 flex flex-col items-center text-center space-y-4 group hover:bg-[#f2ece0]/50 transition-colors"
                    >
                      {/* Minimalist Icon */}
                      <div className="flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <IconRenderer
                          name={pillar.icon}
                          customIconUrl={pillar.customIconUrl}
                          size={iconSize}
                          strokeWidth={iconStrokeWidth}
                          color={iconColor}
                        />
                      </div>

                      {/* Pillar Title */}
                      <h3
                        className="font-sans leading-snug whitespace-pre-line"
                        style={pillarTitleStyle}
                      >
                        {pillar.title}
                      </h3>

                      {/* Pillar Description */}
                      <p className="font-jost leading-relaxed" style={pillarDescStyle}>
                        {pillar.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </ScrollReveal>
          </div>

        </div>
      </div>
    </section>
  );
};
